import React, { useState, useEffect, useCallback } from 'react';
import { 
  StyleSheet, 
  View, 
  Text, 
  FlatList, 
  TouchableOpacity, 
  Alert, 
  ActivityIndicator,
  RefreshControl,
  Dimensions,
  ScrollView
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainTabParamList, MainStackParamList } from '../../navigation/MainNavigator';
import { Routine } from '../../types';
import { colors, typography, spacing, borderRadius } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useFocusEffect } from '@react-navigation/native';
import { useTheme } from '../../context/ThemeContext';
import { CompositeScreenProps } from '@react-navigation/native';
import { useSharedRoutines } from '../../hooks/useSharedRoutines';
import { useNotificationsListener } from '../../hooks/useNotificationsListener';

type RoutinesScreenProps = CompositeScreenProps<
  NativeStackScreenProps<MainTabParamList, 'Routines'>,
  NativeStackScreenProps<MainStackParamList>
>;

// Extender la interfaz para incluir los parámetros de ruta
interface RoutinesScreenParams {
  reload?: boolean;
}

const RoutinesListScreen = ({ navigation, route }: RoutinesScreenProps) => {
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [currentRoutineId, setCurrentRoutineId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [headerRefreshing, setHeaderRefreshing] = useState(false);
  const { theme } = useTheme();
  // Usar el hook para las rutinas compartidas y notificaciones con el parámetro para indicar que estamos en la pantalla de rutinas
  const { sharedRoutines, sharedRoutinesCount, resetNotificationCount } = useSharedRoutines(true);
  const { markRoutineNotificationsAsRead } = useNotificationsListener();
  
  // Detectar si estamos en una pantalla pequeña
  const { width } = Dimensions.get('window');
  const isSmallScreen = width < 375; // iPhone SE o similar

  // Recargar cada vez que la pantalla recibe el foco
  useFocusEffect(
    React.useCallback(() => {
      fetchRoutines();
    }, [])
  );

  useEffect(() => {
    fetchRoutines();
  }, []);

  // Usar useEffect para marcar como leídas las notificaciones al entrar a la pantalla
  useEffect(() => {
    markRoutineNotificationsAsRead();
  }, []);

  // Esta función ahora puede utilizar sharedRoutines en lugar de hacer una consulta adicional
  const fetchRoutines = async () => {
    if (!refreshing && !headerRefreshing) setLoading(true);
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Get routines created by the user
      const routinesQuery = query(
        collection(firestore, 'routines'),
        where('creatorId', '==', userId)
      );
      
      // Get user routines
      const userRoutinesSnapshot = await getDocs(routinesQuery);
      
      // Combine user's routines and shared routines from our hook
      const routinesData: Routine[] = [];
      
      userRoutinesSnapshot.forEach(doc => {
        routinesData.push({ id: doc.id, ...doc.data() } as Routine);
      });
      
      // Add shared routines if they don't already exist in the list
      sharedRoutines.forEach(sharedRoutine => {
        if (!routinesData.some(routine => routine.id === sharedRoutine.id)) {
          routinesData.push(sharedRoutine);
        }
      });
      
      // Get current routine ID
      const userDocRef = doc(firestore, 'users', userId);
      const userDoc = await getDocs(query(collection(firestore, 'users'), where('id', '==', userId)));
      
      if (!userDoc.empty) {
        const userData = userDoc.docs[0].data();
        setCurrentRoutineId(userData.currentRoutineId || null);
      }
      
      setRoutines(routinesData);
    } catch (error) {
      console.error('Error al obtener rutinas:', error);
      Alert.alert('Error', 'No se pudieron cargar las rutinas');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setHeaderRefreshing(false);
    }
  };

  const setAsCurrentRoutine = async (routineId: string) => {
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Update user's current routine
      const userDocRef = doc(firestore, 'users', userId);
      await updateDoc(userDocRef, {
        currentRoutineId: routineId
      });
      
      setCurrentRoutineId(routineId);
      Alert.alert('Éxito', 'Rutina establecida como actual');
    } catch (error) {
      console.error('Error al establecer rutina actual:', error);
      Alert.alert('Error', 'No se pudo establecer la rutina como actual');
    }
  };

  const renderRoutineItem = ({ item }: { item: Routine }) => {
    const isCurrentRoutine = item.id === currentRoutineId;
    const isSharedWithMe = item.authorizedUsers?.includes(auth.currentUser?.uid || '') && item.creatorId !== auth.currentUser?.uid;
    
    return (
      <Card style={styles.routineCard}>
        <View style={styles.routineHeader}>
          <View>
            <Text style={[styles.routineName, { color: theme.colors.text }]}>{item.name}</Text>
            <Text style={[styles.routineDescription, { color: theme.colors.grey.dark }]} numberOfLines={2}>
              {item.description}
            </Text>
          </View>
          
          <View style={styles.badgesContainer}>
            {isCurrentRoutine && (
              <View style={[styles.currentBadge, { backgroundColor: theme.colors.success }]}>
                <Text style={[styles.currentBadgeText, { color: theme.colors.white }]}>Actual</Text>
              </View>
            )}
            
            {isSharedWithMe && (
              <View style={[styles.sharedBadge, { backgroundColor: theme.colors.primary }]}>
                <Text style={[styles.currentBadgeText, { color: theme.colors.white }]}>Compartida</Text>
              </View>
            )}
          </View>
        </View>
        
        <View style={styles.routineInfo}>
          <View style={styles.infoItem}>
            <Ionicons name="calendar-outline" size={16} color={theme.colors.grey.dark} />
            <Text style={[styles.infoText, { color: theme.colors.grey.dark }]}>{item.workoutDaysPerWeek} días</Text>
          </View>
          
          <View style={styles.infoItem}>
            <Ionicons 
              name={item.sharedStatus === 'private' ? 'lock-closed-outline' : 'people-outline'} 
              size={16} 
              color={theme.colors.grey.dark} 
            />
            <Text style={[styles.infoText, { color: theme.colors.grey.dark }]}>
              {item.sharedStatus === 'private' ? 'Privada' : 
               item.sharedStatus === 'public' ? 'Pública' : 'Compartida'}
            </Text>
          </View>
          
          {isSharedWithMe && (
            <View style={styles.infoItem}>
              <Ionicons name="person-outline" size={16} color={theme.colors.grey.dark} />
              <Text style={[styles.infoText, { color: theme.colors.grey.dark }]}>
                Compartida contigo
              </Text>
            </View>
          )}
        </View>
        
        <View style={styles.routineActions}>
          <TouchableOpacity
            style={[styles.iconButton, { backgroundColor: theme.colors.background }]}
            onPress={() => navigation.navigate('EditRoutine', { routineId: item.id })}
          >
            <Ionicons name="eye-outline" size={22} color={theme.colors.primary} />
          </TouchableOpacity>
          
          {!isCurrentRoutine && (
            <Button
              title="Usar rutina"
              onPress={() => setAsCurrentRoutine(item.id)}
              type="primary"
              size="small"
              style={styles.actionButton}
            />
          )}
          
          <Button
            title="Compartir"
            onPress={() => navigation.navigate('RoutineSharing', { routineId: item.id })}
            type="secondary"
            size="small"
            style={styles.actionButton}
          />
        </View>
      </Card>
    );
  };

  // Función para manejar refresh mediante gesto
  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchRoutines();
  }, []);

  // Función para manejar refresh desde el header
  const onHeaderRefresh = useCallback(() => {
    setHeaderRefreshing(true);
    fetchRoutines();
  }, []);

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header with Pull to Refresh */}
      <ScrollView
        style={styles.headerScrollView}
        contentContainerStyle={styles.headerScrollViewContent}
        refreshControl={
          <RefreshControl
            refreshing={headerRefreshing}
            onRefresh={onHeaderRefresh}
            colors={[theme.colors.primary]}
            tintColor={theme.colors.primary}
            progressBackgroundColor={theme.colors.card}
          />
        }
        scrollEnabled={true}
      >
        {/* Header with Actions */}
        <View style={styles.header}>
          <Text style={[styles.title, { color: theme.colors.text }]}>
            {isSmallScreen ? 'Rutinas' : 'Mis Rutinas'}
          </Text>
          
          <View style={styles.headerActions}>
            <TouchableOpacity 
              style={[styles.headerButton, isSmallScreen ? styles.smallScreenButton : null]}
              onPress={() => navigation.navigate('CreateRoutine')}
            >
              <Ionicons name="add-circle" size={isSmallScreen ? 22 : 24} color={theme.colors.primary} />
              {!isSmallScreen && (
                <Text style={[styles.headerButtonText, { color: theme.colors.primary }]}>Crear</Text>
              )}
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.headerButton, isSmallScreen ? styles.smallScreenButton : null]}
              onPress={() => navigation.navigate('JoinRoutine')}
            >
              <Ionicons name="link" size={isSmallScreen ? 22 : 24} color={theme.colors.primary} />
              {!isSmallScreen && (
                <Text style={[styles.headerButtonText, { color: theme.colors.primary }]}>Unirse</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
        
        {/* Pull to refresh hint when headerRefreshing is true */}
        {headerRefreshing && (
          <Text style={[styles.refreshHint, { color: theme.colors.grey.medium }]}>
            Desliza hacia abajo para refrescar...
          </Text>
        )}
      </ScrollView>
      
      {/* Routines List */}
      {loading && !refreshing && !headerRefreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      ) : routines.length > 0 ? (
        <FlatList
          data={routines}
          renderItem={renderRoutineItem}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.routinesList}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[theme.colors.primary]}
              tintColor={theme.colors.primary}
              progressBackgroundColor={theme.colors.card}
            />
          }
        />
      ) : (
        <View style={styles.emptyContainer}>
          <Ionicons name="list-outline" size={64} color={theme.colors.grey.medium} />
          <Text style={[styles.emptyText, { color: theme.colors.text }]}>No tienes rutinas</Text>
          <Text style={[styles.emptySubtext, { color: theme.colors.grey.dark }]}>
            Crea tu primera rutina de entrenamiento o únete a una compartida
          </Text>
          <Button
            title="Crear Rutina"
            onPress={() => navigation.navigate('CreateRoutine')}
            type="primary"
            style={styles.emptyButton}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: spacing.md,
  },
  headerScrollView: {
    maxHeight: 100,
    zIndex: 10,
  },
  headerScrollViewContent: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: typography.fontSize.xxl,
    fontWeight: 'bold',
  },
  headerActions: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  headerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    padding: spacing.sm,
    borderRadius: borderRadius.sm,
    backgroundColor: `${colors.grey.light}30`,
  },
  headerButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  refreshHint: {
    textAlign: 'center',
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs,
    marginBottom: spacing.md,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  emptyText: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    marginTop: spacing.md,
  },
  emptySubtext: {
    fontSize: typography.fontSize.md,
    textAlign: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.lg,
  },
  emptyButton: {
    width: '100%',
    maxWidth: 250,
  },
  routinesList: {
    paddingBottom: spacing.lg,
  },
  routineCard: {
    marginBottom: spacing.md,
  },
  routineHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  routineName: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    marginBottom: spacing.xs,
  },
  routineDescription: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.sm,
    maxWidth: '85%',
  },
  currentBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: borderRadius.sm,
  },
  currentBadgeText: {
    fontSize: typography.fontSize.xs,
    fontWeight: 'bold',
  },
  badgesContainer: {
    flexDirection: 'column',
    gap: spacing.xs,
  },
  sharedBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: borderRadius.sm,
  },
  routineInfo: {
    flexDirection: 'row',
    marginBottom: spacing.md,
    gap: spacing.md,
    flexWrap: 'wrap',
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  infoText: {
    fontSize: typography.fontSize.sm,
  },
  routineActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: spacing.md,
    marginTop: spacing.md,
  },
  actionButton: {
    minWidth: 90,
    borderRadius: borderRadius.sm,
  },
  iconButton: {
    padding: spacing.sm,
    borderRadius: borderRadius.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  smallScreenButton: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
});

export default RoutinesListScreen; 